<template>
    <button
        type="button"
        class="csv-poblacion-btn"
        :disabled="disabled"
        :title="disabled ? 'No hay capas de población para exportar' : 'Descargar desglose de población por manzana censal'"
        @click="download"
    >
        <font-awesome-icon v-if="loading" icon="spinner" spin />
        <font-awesome-icon v-else icon="download" />
        {{ loading ? 'Descargando…' : 'Descargar datos' }}
    </button>
</template>

<script>
import { attributesTableService } from "@/services/layers/attributesTable.js";
import { getProp, fetchPopulationFeatures } from "./populationFeatures.js";

const CSV_HEADERS = [
    "Distancia (metros)",
    "Tipo de Población",
    "Región",
    "Provincia",
    "Comuna",
    "Nombre",
    "Código INE",
    "Total",
    "Total de niños",
    "Adultos",
    "Total de adultos mayores",
    "Total de hombres",
    "Total de mujeres",
    "Total de viviendas",
];

const csvEscape = (value) => {
    const str = value == null ? "" : String(value);
    return /[",\n\r]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

const jsonToCsv = (rows, headers) => {
    const lines = [headers.join(",")];
    for (const row of rows) {
        lines.push(headers.map((header) => csvEscape(row[header])).join(","));
    }
    return lines.join("\r\n");
};

export default {
    name: "BotonDesacargaCSV",
    props: {
        filterGeometries: { type: Array, default: () => [] },
        populationLayers: { type: Array, default: () => [] },
        populationMethod: { type: String, default: "polygon" },
    },
    data() {
        return {
            loading: false,
        };
    },
    computed: {
        disabled() {
            return this.loading || !this.populationLayers.length || !this.filterGeometries.length;
        },
    },
    methods: {
        buildRow(props, layerName, radius) {
            return {
                "Distancia (metros)": radius != null ? radius : "",
                "Tipo de Población": layerName,
                "Región": getProp(props, "region"),
                "Provincia": getProp(props, "provincia"),
                "Comuna": getProp(props, "comuna"),
                "Nombre": getProp(props, "nom_ent"),
                "Código INE": getProp(props, "manzent"),
                "Total": getProp(props, "tot_pob"),
                "Total de niños": getProp(props, "ninos"),
                "Adultos": getProp(props, "adultos"),
                "Total de adultos mayores": getProp(props, "ancianos"),
                "Total de hombres": getProp(props, "hombres"),
                "Total de mujeres": getProp(props, "mujeres"),
                "Total de viviendas": getProp(props, "tot_viv"),
            };
        },
        async download() {
            if (this.disabled) return;
            this.loading = true;
            try {
                const rows = [];
                for (const geometry of this.filterGeometries) {
                    for (const layer of this.populationLayers) {
                        const layerName = layer.category_name || layer.name || "";
                        const features = await fetchPopulationFeatures(layer, geometry, this.populationMethod);
                        for (const feature of features) {
                            const props = feature.properties || feature.attributes || {};
                            rows.push(this.buildRow(props, layerName, geometry.radius));
                        }
                    }
                }
                if (!rows.length) {
                    this.$store.dispatch("toast/show", { message: "No se encontró población en el área analizada.", type: "warning" });
                    return;
                }
                const csv = jsonToCsv(rows, CSV_HEADERS);
                attributesTableService.triggerDownload(`poblacion_geoproceso_${Date.now()}.csv`, csv, "text/csv;charset=utf-8;");
            } catch (error) {
                console.error("Error generando CSV de población:", error);
                this.$store.dispatch("toast/show", { message: "Error al generar el CSV de población.", type: "error" });
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style scoped>
.csv-poblacion-btn {
    display: inline-flex;
    align-items: center;
    gap: 7px;

    min-height: 32px;
    padding: 6px 14px;

    background: var(--color-white, #fff);
    border: 1px solid var(--color-line, #dbe1e6);
    border-radius: 4px;

    color: var(--color-navy, #0e2a40);
    font-size: 0.8rem;
    font-weight: 600;

    cursor: pointer;

    transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
}

.csv-poblacion-btn:hover:not(:disabled) {
    background: var(--color-fog, #dbe1e6);
    border-color: var(--color-steel, #3b6e94);
}

.csv-poblacion-btn:disabled {
    background: var(--color-fog, #dbe1e6);
    border-color: var(--color-line, #dbe1e6);
    color: var(--color-ghost, #8a929a);
    cursor: not-allowed;
}
</style>
